import React, { useMemo, useState, useCallback, useRef } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { ZoomIn, ZoomOut, RotateCcw, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toOfficialCategoryName } from '@/utils/edtechCategoryNames';
import { getDealCategories, normalizeCategoryValue } from '@/utils/procurementCategoryUtils';
import { toCanonicalVendorName, TENANT_OWN_PRODUCTS } from '@/lib/procurementCompetitors';
import { isDealVisible } from '@/lib/procurementVisibility';
import { useTranslation } from '@/components/utils/translations';

const X_MAX = 100000;
const Y_MAX_STATIC = 200;

function toNumberSafe(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const s = String(value).trim();
  if (!s) return 0;

  // remove currency symbols, commas, spaces
  const cleaned = s.replace(/[$£€,\s]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/** Compute Y axis cap from data max: add ~10% buffer, round to nice number, clamp 5–200 */
function computeYAxisCap(dataMax) {
  if (!dataMax || dataMax <= 0) return 10;
  const buffered = dataMax * 1.1;
  const step = dataMax < 20 ? 2 : dataMax < 100 ? 5 : 10;
  const cap = Math.ceil(buffered / step) * step;
  return Math.min(Math.max(cap, 5), Y_MAX_STATIC);
}

const BRAND_COLORS = ['#00C2C2', '#0A1A2F', '#1C485C', '#3D8C91', '#56B0AF', '#89CDD4', '#C6F2F0', '#ECFFFE'];
/* [2026-11-03] Trend by vendor draws one line per supplier family and can show 15+ of them; the eight
   teal brand shades were indistinguishable at that count, so vendor lines get a categorical palette. */
const VENDOR_COLORS = [
  '#00C2C2', '#0A1A2F', '#E4572E', '#7B61FF', '#F4A259', '#2E86AB', '#A23B72', '#3BB273',
  '#F15BB5', '#6C757D', '#C9A227', '#1B998B', '#E36414', '#5F0F40', '#4361EE', '#8AC926',
];

/* [2026-11-03] SUPPLIER FAMILY, NOT THE RAW SUPPLIER STRING (Tiago). The server now sends
   `supplier_family` / `supplier_family_key` from vendor_families with legal-entity and country-branch
   spellings folded (EBSCO's 39 spellings are one key). When it is absent — the own-closed-deals path,
   or an API that predates the field — fall back to the canonical name with its trailing legal form
   removed, so "ConfigBit, Lda" and "ConfigBit" still share a line. */
const FE_TRAILING_LEGAL = /[\s,]+(s\.?\s?l\.?\s?u\.?|s\.?\s?a\.?\s?u\.?|s\.?\s?a\.?\s?s\.?|s\.?\s?r\.?\s?l\.?|s\.?\s?p\.?\s?a\.?|s\.?\s?l\.?|s\.?\s?a\.?|ltda?\.?|lda\.?|limited|ltd\.?|inc\.?|llc|gmbh|ag|b\.?\s?v\.?|n\.?\s?v\.?|plc|pty\.?|sarl|co\.?|corp\.?|corporation|company)\.?$/i;
function feFamilyLabel(name) {
  let s = String(name || '').trim();
  for (let i = 0; i < 4; i++) {
    const next = s.replace(FE_TRAILING_LEGAL, '').trim();
    if (next === s || !next) break;
    s = next;
  }
  return s;
}

const RULE_MODES = ['all', 'exclude', 'only'];

function getYearLabel(deal) {
  const d = deal.first_award_date ?? deal.latest_renewal_date ?? deal.published_date ?? deal.created_at;
  if (!d) return null;
  try {
    const y = new Date(d).getFullYear();
    return isNaN(y) ? null : String(y);
  } catch {
    return null;
  }
}

/** Exclude Canvas, D2L, Anthology from Online Exam Platforms benchmarking only */
function isExcludedForOnlineExamBenchmark(deal) {
  const cats = getDealCategories(deal);
  const isOnlineExam = cats.some((cat) => {
    const catLower = (cat || '').toLowerCase();
    return catLower.includes('online exam') || catLower.includes('online_exam');
  });
  if (!isOnlineExam) return false;
  /* [2026-10-13] MATCH THE SUPPLIER, NOT THE PRODUCT STRING.
   * This tested `supplier + ' ' + product_name`, so an online-exam vendor was blocked for
   * NAMING the LMS it integrates with. Of the 4 deals the rule removed, THREE were ExamSoft:
   *   ExamSoft              "ExamSoft + Canvas"      Griffith University
   *   EXAMSOFT WORLDWIDE INC "ExamSoft + Blackboard" Western Sydney University
   *   ExamSoft              "ExamSoft + Canvas"      University of British Columbia
   * Only the fourth — Instructure, "licenses to Instructure's Canvas Catalog" — is what the
   * rule is for. The intent is to keep LMS VENDORS out of online-exam price benchmarking, and
   * that is a fact about who the supplier is, not about which LMS the product plugs into. */
  const supplier = (deal.supplier ?? '').trim().toLowerCase();
  const excludeVendors = ['canvas', 'instructure', 'd2l', 'brightspace', 'desire2learn', 'anthology', 'blackboard'];
  return excludeVendors.some((kw) => supplier.includes(kw));
}

/** Normalize deal to benchmark point — uses deal-level fields only (no account) */
function getBenchmarkPoint(deal, selectedCategories = []) {
  if (!isDealVisible(deal)) return null;
  if (isExcludedForOnlineExamBenchmark(deal)) return null;

  const enrolment = toNumberSafe(
    deal.enrolment ?? deal.enrollment ?? deal.student_count ?? deal.total_students
  );
  /* [2026-10-13] USD ONLY. The old chain fell through to deal_value / value / contract_value,
   * which are in the deal's NATIVE currency, and plotted a GBP or SGD figure as dollars — the
   * same defect fixed on Recently-Closed and the map. 11 rows hit it. It also fell through to
   * neo_estimated_value, a NEO MODEL output, and plotted it indistinguishably from a real
   * contract price. The server path (procurement_benchmark_points) already requires
   * deal_value_usd > 0; this aligns the client path with it. */
  const valueUsd = toNumberSafe(deal.deal_value_usd);
  const supplier = (deal.supplier || '').trim();
  const institutionType = deal.institution_type || null;

  if (!supplier || enrolment <= 0 || valueUsd <= 0) return null;

  /* [2026-10-13] ANNUALISE. This was deal_value_usd / enrolment with NO division by the
   * contract term, while the tooltip has always called it "$/student/year". initial_term is in
   * MONTHS, and only 12% of plotted deals are 12-month — 34% are multi-year, 19% under a year,
   * 34% unknown. Measured on LMS: a 60-month deal plotted at a median $104.16/student against
   * $12.60 for a 12-month one (8.3x), when annualised it is $20.83 vs $12.60 and the 36-month
   * cohort is the CHEAPEST at $9.37. The axis inverted the real ranking.
   *
   * A point whose term is unknown cannot be placed on a per-year axis without inventing the
   * term, so it is excluded rather than assumed to be 12 months. The caller counts the
   * exclusions and says so under the chart — dropping a third of the data silently would be
   * worse than the bug. `initial_term` is TEXT and carries non-numeric values. */
  const termMonths = toNumberSafe(deal.initial_term);
  if (!(termMonths > 0)) return { _excluded: 'term_unknown' };
  const perStudentPerYear = (valueUsd / enrolment) / (termMonths / 12);

  const vendor = toCanonicalVendorName(supplier, deal.category);
  const covered = getDealCategories(deal);
  const selected = (selectedCategories || []).map((c) => normalizeCategoryValue(c)).filter(Boolean);
  const matched = selected.find((c) => covered.includes(c));
  const categoryDisplay = matched || covered[0] || deal.category || '—';
  const year = getYearLabel(deal) || 'Unknown';
  const familyLabel = String(deal.supplier_family || '').trim()
    || feFamilyLabel(vendor && vendor !== 'Unknown' ? vendor : supplier);
  const familyKey = deal.supplier_family_key || `l:${familyLabel.toLowerCase()}`;

  return {
    supplierFamily: familyLabel,
    familyKey,
    enrolment: Math.min(enrolment, X_MAX),
    enrolmentRaw: enrolment,
    usdPerStudent: Math.round(perStudentPerYear * 100) / 100,
    usdPerStudentRaw: perStudentPerYear,
    termMonths,
    contractTotalPerStudent: Math.round((valueUsd / enrolment) * 100) / 100,
    year,
    name: deal.account_name ?? deal.institution ?? deal.institution_name ?? 'Unknown',
    category: categoryDisplay,
    institutionType: institutionType || '—',
    vendor: vendor && vendor !== 'Unknown' ? vendor : supplier,
    rawVendor: supplier,
  };
}

/** Is this vendor the tenant's own? Same list and containment rule as the competitor filters. */
function makeIsOwnVendor(tenantId) {
  const own = (TENANT_OWN_PRODUCTS[String(tenantId || '').toLowerCase().trim()] || [])
    .map((p) => p.toLowerCase().trim()).filter((p) => p.length >= 3);
  return (name) => {
    const n = String(name || '').toLowerCase().trim();
    return !!n && own.some((p) => n === p || n.includes(p));
  };
}

/* Trend-by-vendor hover: every vendor's price that year, highest first, with the tenant's own vendor
   marked so a rep can see at a glance where they sit (Tiago, 2026-10-15). */
function VendorTrendTooltip({ active, payload, label, isOwnVendor, focused }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const rows = payload
    .filter((p) => p && p.value != null && Number.isFinite(Number(p.value)))
    .sort((a, b) => Number(b.value) - Number(a.value));
  if (!rows.length) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-semibold text-[#0A1A2F]">{t('procYearLabel', { year: label })}</div>
      {rows.filter((p, i) => i < 15 || p.dataKey === focused || isOwnVendor(p.name ?? p.dataKey)).map((p) => {
        const own = isOwnVendor(p.name ?? p.dataKey);
        const isFocused = focused && (p.dataKey === focused);
        const dimmed = focused && !isFocused;
        return (
          <div key={p.dataKey}
            className={`flex items-center justify-between gap-4 rounded px-1 py-0.5 ${own ? 'bg-[#E6F8F8] font-semibold text-[#007F80]' : isFocused ? 'bg-gray-100 font-semibold text-[#0A1A2F]' : dimmed ? 'text-gray-400' : 'text-gray-700'}`}>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color }} aria-hidden="true" />
              {p.name ?? p.dataKey}{own ? ` (${t('procYou')})` : ''}
            </span>
            <span className="font-mono tabular-nums">${Number(p.value).toFixed(2)}</span>
          </div>
        );
      })}
    </div>
  );
}

function BenchmarkingScatter({
  tenantId,
  points: pointsProp,
  loading = false,
  error = null,
  categories = [],
  institutionTypes = [],
  vendors = [],
  selectedVendor,
}) {
  const { t } = useTranslation();
  const isOwnVendor = useMemo(() => makeIsOwnVendor(tenantId), [tenantId]);
  const [hoveredYear, setHoveredYear] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0.5, y: 0.5 });
  const [viewMode, setViewMode] = useState('scatter'); // 'scatter' | 'trend' | 'trend_vendor'
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);
  const chartContainerRef = useRef(null);

  const { scatterData, years, yExtent, excludedNoTerm } = useMemo(() => {
    const _raw = (pointsProp || []).map((deal) => getBenchmarkPoint(deal, categories)).filter(Boolean);
    const excludedNoTerm = _raw.filter((p) => p && p._excluded === 'term_unknown').length;
    const points = _raw.filter((p) => p && !p._excluded);
    const yearSet = new Set(points.map((p) => p.year).filter(Boolean));
    const yearsList = Array.from(yearSet).sort();
    const yMin = points.length ? Math.min(...points.map((p) => p.usdPerStudent)) : 0;
    const yMax = points.length ? Math.max(...points.map((p) => p.usdPerStudent)) : 100;
    return { scatterData: points, years: yearsList, yExtent: [yMin, yMax], excludedNoTerm };
  }, [pointsProp, categories]);

  /* One label per supplier family: the spelling most points carry. Two keys that end up with the same
     label (a family and an unmatched supplier spelled alike) are kept apart rather than merged.
     [2026-11-03] When a quarter or more of a family's points in view were sold under a different BRAND,
     the label names both, "Turnitin (ExamSoft)" — 74 of Turnitin's 82 Online Exams deals are ExamSoft,
     the name a rep knows that competitor by (Tiago); with Marking also selected it is 74 of ~227, which
     a majority rule hid. A spelling of the family itself (Watermark Insights,
     ATI - Assessment Technologies Institute) is not a brand. */
  const familyMeta = useMemo(() => {
    const letters = (s) => String(s || '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
    const m = new Map();
    scatterData.forEach((p) => {
      const e = m.get(p.familyKey) || { labels: new Map(), brands: new Map(), count: 0 };
      e.count += 1;
      e.labels.set(p.supplierFamily, (e.labels.get(p.supplierFamily) || 0) + 1);
      const brand = feFamilyLabel(p.rawVendor);
      const b = letters(brand);
      const f = letters(p.supplierFamily);
      // A name carrying a legal form is the COMPANY, not a brand: "Smiley Owl Tech S.L." is SMOWL.
      const isLegalEntity = brand !== String(p.rawVendor || '').trim();
      if (b && f && !isLegalEntity && !b.includes(f) && !f.includes(b)) e.brands.set(brand, (e.brands.get(brand) || 0) + 1);
      m.set(p.familyKey, e);
    });
    const out = new Map();
    const used = new Set();
    [...m.entries()].sort((a, b) => b[1].count - a[1].count).forEach(([k, e]) => {
      let label = [...e.labels.entries()].sort((a, b) => b[1] - a[1])[0][0] || k;
      const [brand, brandCount] = [...e.brands.entries()].sort((a, b) => b[1] - a[1])[0] || [];
      if (brand && brandCount * 4 >= e.count) label = `${label} (${brand})`;
      if (used.has(label)) label = `${label} (${String(k).slice(2)})`;
      used.add(label);
      out.set(k, { label, count: e.count });
    });
    return out;
  }, [scatterData]);
  const familyLabelOf = useCallback(
    (p) => familyMeta.get(p.familyKey)?.label || p.supplierFamily || p.vendor,
    [familyMeta]
  );

  /* [2026-11-03] TREND BY CATEGORY — A SUPPLIER RULE (Tiago). Some suppliers are not aligned with the
     category being benchmarked (a reseller's mixed basket, a vendor priced on a different basis), so
     the user can EXCLUDE chosen suppliers or keep ONLY chosen ones. It applies to this one chart and
     is remembered per tenant and category selection in the browser (a per-viewer preference, so
     localStorage; it never changes the data or anyone else's view). */
  const ruleStorageKey = useMemo(
    () => `neo.bench.trendCategoryRule.${tenantId || '_'}.${[...(categories || [])].map(String).sort().join('|')}`,
    [tenantId, categories]
  );
  const [supplierRule, setSupplierRule] = useState({ mode: 'all', keys: [] });
  const [ruleSearch, setRuleSearch] = useState('');
  React.useEffect(() => {
    let next = { mode: 'all', keys: [] };
    try {
      const raw = window.localStorage.getItem(ruleStorageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && RULE_MODES.includes(parsed.mode) && Array.isArray(parsed.keys)) next = { mode: parsed.mode, keys: parsed.keys };
    } catch { /* storage unavailable: start from no rule */ }
    setSupplierRule(next);
  }, [ruleStorageKey]);
  const saveRule = useCallback((next) => {
    setSupplierRule(next);
    try {
      if (next.mode === 'all' && !next.keys.length) window.localStorage.removeItem(ruleStorageKey);
      else window.localStorage.setItem(ruleStorageKey, JSON.stringify(next));
    } catch { /* a private window: the rule still applies for this visit */ }
  }, [ruleStorageKey]);
  const ruleOptions = useMemo(
    () => [...familyMeta.entries()].map(([key, v]) => ({ key, label: v.label, count: v.count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
    [familyMeta]
  );
  const ruleActive = supplierRule.mode !== 'all' && supplierRule.keys.length > 0;
  /* [2026-11-04] Both TREND views leave out deals shorter than MIN_TREND_TERM_MONTHS (Tiago); the
     scatter keeps them. A 1-3 month span cannot be annualised honestly: most are register award-to-
     completion gaps (959 visible Texas LBB deals carry 1-3 months), and even a real one — a month of
     exam fees, a 16-day consulting job — multiplies 4-12x when spread over a year. */
  const MIN_TREND_TERM_MONTHS = 4;
  const trendPoints = useMemo(
    () => scatterData.filter((p) => p.termMonths >= MIN_TREND_TERM_MONTHS),
    [scatterData]
  );
  const trendCategoryPoints = useMemo(() => {
    if (!ruleActive) return trendPoints;
    const set = new Set(supplierRule.keys);
    return trendPoints.filter((p) => (supplierRule.mode === 'exclude' ? !set.has(p.familyKey) : set.has(p.familyKey)));
  }, [trendPoints, supplierRule, ruleActive]);

  const [focusedVendor, setFocusedVendor] = useState(null);

  /* [2026-11-03] YEARLY MEDIAN, not mean (Tiago: evasys_V2 Feedback & Evaluation jumped in 2024).
     One deal moved the whole year: MD Anderson's $1.89M organisation-wide Qualtrics licence over
     355 students plots at $2,899/student/yr and took the 2024 mean to $47.77 against a median of
     $2.81 (every other year's mean sits at $2-11). Per-student prices span two orders of magnitude,
     so any single mis-based row can do this; the median cannot be moved by one point. */
  const trendByCategory = useMemo(() => {
    const byYearCategory = {};
    const byYearVendorSel = {};
    const byYearVendorOth = {};
    const median = (vals) => {
      const s = [...vals].sort((a, b) => a - b);
      const m = s.length >> 1;
      return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
    };
    const round2 = (v) => Math.round(v * 100) / 100;
    trendCategoryPoints.forEach((p) => {
      if (p.year === 'Unknown') return;
      const key = `${p.year}|${p.category}`;
      const val = p.usdPerStudentRaw ?? p.usdPerStudent;
      if (!byYearCategory[key]) byYearCategory[key] = { year: p.year, category: p.category, vals: [] };
      byYearCategory[key].vals.push(val);
      if (selectedVendor && p.vendor && p.vendor !== '—') {
        const yr = p.year;
        const bucket = p.vendor === selectedVendor ? byYearVendorSel : byYearVendorOth;
        (bucket[yr] ||= []).push(val);
      }
    });
    const avgs = Object.values(byYearCategory).map((o) => ({
      year: o.year,
      [o.category]: round2(median(o.vals)),
    }));
    const byYear = {};
    avgs.forEach((r) => {
      if (!byYear[r.year]) byYear[r.year] = { year: r.year };
      Object.entries(r).forEach(([k, v]) => {
        if (k !== 'year' && typeof v === 'number') byYear[r.year][k] = v;
      });
    });
    let vendorData = [];
    if (selectedVendor && (Object.keys(byYearVendorSel).length > 0 || Object.keys(byYearVendorOth).length > 0)) {
      const yearsUsed = [...new Set([...Object.keys(byYearVendorSel), ...Object.keys(byYearVendorOth)])].sort((a, b) => Number(a) - Number(b));
      vendorData = yearsUsed.map((yr) => ({
        year: yr,
        [selectedVendor]: byYearVendorSel[yr] ? round2(median(byYearVendorSel[yr])) : null,
        Others: byYearVendorOth[yr] ? round2(median(byYearVendorOth[yr])) : null,
      }));
    }
    const categoriesUsed = [...new Set(avgs.map((a) => Object.keys(a).filter((k) => k !== 'year')).flat())].filter((c) => c && c !== '—').sort();
    return {
      data: Object.values(byYear).sort((a, b) => Number(a.year) - Number(b.year)),
      categories: categoriesUsed,
      vendorData,
      showVendorTrend: !!selectedVendor && vendorData.length > 0,
    };
  }, [trendCategoryPoints, selectedVendor]);

  const MIN_DEALS_FOR_VENDOR_TREND = 8;

  /* [2026-11-03] Grouped on the SUPPLIER FAMILY (familyLabelOf), not the product-flavoured vendor
     label, so a supplier's legal entities and country branches draw one line. */
  const trendByVendor = useMemo(() => {
    const isInternal = (p) => (p.rawVendor || p.vendor || '').toLowerCase().includes('internal');
    const excludedVendors = new Set(['moodle', 'built-in lms']);
    const isExcludedVendor = (p) => excludedVendors.has((familyLabelOf(p) || '').toLowerCase())
      || excludedVendors.has((p.vendor || '').toLowerCase());

    const OUTLIER_MULTIPLIER = 2.5;
    const enrolmentBins = [0, 10000, 25000, 50000, 75000, X_MAX + 1];
    const getBin = (enrol) => {
      for (let i = 0; i < enrolmentBins.length - 1; i++) {
        if (enrol >= enrolmentBins[i] && enrol < enrolmentBins[i + 1]) return i;
      }
      return enrolmentBins.length - 2;
    };

    const eligibleForVendorTrend = trendPoints.filter(
      (p) => p.year !== 'Unknown' && p.vendor && p.vendor !== '—' && !isInternal(p) && !isExcludedVendor(p)
    );
    const binSums = {};
    const binCounts = {};
    eligibleForVendorTrend.forEach((p) => {
      const b = getBin(p.enrolment ?? p.enrolmentRaw ?? 0);
      if (!binSums[b]) binSums[b] = 0;
      binSums[b] += p.usdPerStudentRaw ?? p.usdPerStudent;
      binCounts[b] = (binCounts[b] || 0) + 1;
    });
    const binAvg = {};
    Object.keys(binSums).forEach((b) => {
      binAvg[b] = binCounts[b] ? binSums[b] / binCounts[b] : 0;
    });
    const isOutlier = (p) => {
      const b = getBin(p.enrolment ?? p.enrolmentRaw ?? 0);
      const avg = binAvg[b];
      if (!avg || avg <= 0) return false;
      const val = p.usdPerStudentRaw ?? p.usdPerStudent;
      return val > OUTLIER_MULTIPLIER * avg;
    };

    const vendorCounts = {};
    eligibleForVendorTrend.forEach((p) => {
      if (isOutlier(p)) return;
      const v = familyLabelOf(p);
      vendorCounts[v] = (vendorCounts[v] || 0) + 1;
    });
    const qualifyingVendors = new Set(
      Object.entries(vendorCounts).filter(([, n]) => n >= MIN_DEALS_FOR_VENDOR_TREND).map(([v]) => v)
    );

    const byYearVendor = {};
    eligibleForVendorTrend.forEach((p) => {
      const v = familyLabelOf(p);
      if (isOutlier(p) || !qualifyingVendors.has(v)) return;
      const key = `${p.year}|${v}`;
      if (!byYearVendor[key]) byYearVendor[key] = { year: p.year, vendor: v, vals: [] };
      byYearVendor[key].vals.push(p.usdPerStudentRaw ?? p.usdPerStudent);
    });
    // [2026-11-03] Yearly MEDIAN per supplier, as on Trend by category (Tiago): a supplier's line
    // spiked to ~$50 in 2024 on a mean over a handful of deals.
    const median = (vals) => {
      const s = [...vals].sort((a, b) => a - b);
      const m = s.length >> 1;
      return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
    };
    const avgs = Object.values(byYearVendor).map((o) => ({
      year: o.year,
      [o.vendor]: Math.round(median(o.vals) * 100) / 100,
    }));
    const byYear = {};
    avgs.forEach((r) => {
      if (!byYear[r.year]) byYear[r.year] = { year: r.year };
      Object.entries(r).forEach(([k, v]) => {
        if (k !== 'year' && typeof v === 'number') byYear[r.year][k] = v;
      });
    });
    // Most deals first: that order decides which lines are drawn by default and the legend order.
    const vendorsUsed = [...qualifyingVendors].sort((a, b) => (vendorCounts[b] - vendorCounts[a]) || a.localeCompare(b));
    return {
      data: Object.values(byYear).sort((a, b) => Number(a.year) - Number(b.year)),
      vendors: vendorsUsed,
      counts: vendorCounts,
    };
  }, [trendPoints, familyLabelOf]);

  /* [2026-11-03] A market like IT Hardware qualifies ~100 suppliers for this chart, and 100 lines plus
     a 100-entry recharts legend squashed the plot into a strip. Draw the TOP_VENDOR_LINES suppliers by
     deal count, plus the one in focus and the tenant's own; every qualifying supplier stays listed in
     the legend below and is drawn the moment it is clicked. */
  const TOP_VENDOR_LINES = 12;
  const drawnVendors = useMemo(() => {
    const top = trendByVendor.vendors.slice(0, TOP_VENDOR_LINES);
    const extra = trendByVendor.vendors.filter((v) => !top.includes(v) && (v === focusedVendor || isOwnVendor(v)));
    return [...top, ...extra];
  }, [trendByVendor.vendors, focusedVendor, isOwnVendor]);

  // A focus that no longer names a drawn line (filters changed) is dropped rather than dimming everything.
  React.useEffect(() => {
    if (focusedVendor && !trendByVendor.vendors.includes(focusedVendor)) setFocusedVendor(null);
  }, [focusedVendor, trendByVendor.vendors]);
  const vendorColor = useCallback(
    (v) => VENDOR_COLORS[Math.max(0, drawnVendors.indexOf(v)) % VENDOR_COLORS.length],
    [drawnVendors]
  );

  const yAxisCapScatter = useMemo(() => computeYAxisCap(yExtent[1]), [yExtent]);

  const yAxisCapTrend = useMemo(() => {
    const data = viewMode === 'trend_vendor' ? trendByVendor.data : (trendByCategory.showVendorTrend ? trendByCategory.vendorData : trendByCategory.data);
    if (!data?.length) return 50;
    let maxVal = 0;
    // Scale to the lines actually drawn, so an undrawn supplier's spike cannot flatten the chart.
    const drawn = viewMode === 'trend_vendor' ? new Set(drawnVendors) : null;
    data.forEach((row) => {
      Object.entries(row).forEach(([k, v]) => {
        if (k !== 'year' && typeof v === 'number' && v > maxVal && (!drawn || drawn.has(k))) maxVal = v;
      });
    });
    return computeYAxisCap(maxVal);
  }, [viewMode, trendByVendor.data, trendByCategory.data, trendByCategory.vendorData, trendByCategory.showVendorTrend, drawnVendors]);

  const xDomain = useMemo(() => {
    const centerX = pan.x * X_MAX;
    const halfRange = (X_MAX / 2) / zoom;
    const lo = Math.max(0, centerX - halfRange);
    const hi = Math.min(X_MAX, centerX + halfRange);
    return [lo, hi];
  }, [zoom, pan.x]);

  const yDomain = useMemo(() => {
    const [yMin, yMax] = yExtent;
    const cap = yAxisCapScatter;
    const range = Math.max(Math.min(yMax, cap) - yMin, 1);
    const centerY = yMin + pan.y * range;
    const halfRange = (range / 2) / zoom;
    const lo = Math.max(0, yMin - 2);
    const hi = Math.min(cap, yMax + 2);
    return [Math.max(lo, centerY - halfRange), Math.min(hi, centerY + halfRange)];
  }, [zoom, pan.y, yExtent, yAxisCapScatter]);

  const handleZoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.5, 8)), []);
  const handleZoomOut = useCallback(() => setZoom((z) => Math.max(z - 0.5, 0.5)), []);
  const handleReset = useCallback(() => {
    setZoom(1);
    setPan({ x: 0.5, y: 0.5 });
  }, []);

  React.useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const handler = (e) => {
      if (viewMode !== 'scatter') return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.3 : 0.3;
      setZoom((z) => Math.min(8, Math.max(0.5, z + delta)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [viewMode]);

  const handleMouseDown = useCallback((e) => {
    if (viewMode !== 'scatter' || e.button !== 0) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  }, [viewMode]);

  const handleMouseMove = useCallback((e) => {
    if (!dragStartRef.current) return;
    const { x: sx, y: sy } = dragStartRef.current;
    const dx = (e.clientX - sx) / 250;
    const dy = (e.clientY - sy) / 250;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    setPan((prev) => ({
      x: Math.max(0, Math.min(1, prev.x - dx)),
      y: Math.max(0, Math.min(1, prev.y + dy)),
    }));
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  React.useEffect(() => {
    if (!isDragging) return;
    const onMove = handleMouseMove;
    const onUp = handleMouseUp;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const yearColors = useMemo(() => {
    const m = {};
    years.forEach((y, i) => {
      m[y] = BRAND_COLORS[i % BRAND_COLORS.length];
    });
    m['Unknown'] = '#9ca3af';
    return m;
  }, [years]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <div className="bg-[#0A1A2F] text-white text-xs rounded-lg shadow-xl px-3 py-2 border border-[#00C2C2]/30 max-w-[220px]">
        <div className="font-semibold mb-1 truncate" title={p.name}>{p.name}</div>
        <div>{t('procEnrolment')}: {(p.enrolmentRaw ?? p.enrolment).toLocaleString()}</div>
        <div>{t('procUsdPerStudentYear')}: ${p.usdPerStudent.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        {p.termMonths ? (
          <div className="text-gray-500">
            {t('procOverTermWholeContract', { months: p.termMonths, amount: `$${p.contractTotalPerStudent?.toLocaleString(undefined, { maximumFractionDigits: 2 })}` })}
          </div>
        ) : null}
        <div className="text-[#89CDD4]">{t('procYearLabel', { year: p.year })}</div>
        {p.category !== '—' && <div className="text-gray-400">{p.category}</div>}
        {p.vendor && p.vendor !== '—' && <div className="text-gray-400">{t('vendor')}: {familyLabelOf(p)}</div>}
      </div>
    );
  };

  if (loading && scatterData.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-12 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (scatterData.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
        <p className="text-gray-500">
          {t('procBenchEmptyIntro')} <strong>{t('procBenchFieldEnrolment')}</strong>,{' '}
          <strong>{t('procBenchFieldDealValue')}</strong>, <strong>{t('procBenchFieldSupplier')}</strong>, {t('procBenchAndANumeric')}{' '}
          <strong>{t('procBenchFieldInitialTerm')}</strong> {t('procBenchEmptyOutro')}
        </p>
        {excludedNoTerm > 0 ? (
          <p className="text-gray-400 text-xs mt-2">
            {t(excludedNoTerm === 1 ? 'procBenchExcludedNoTermOne' : 'procBenchExcludedNoTermMany', { n: excludedNoTerm.toLocaleString() })}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b bg-[#ECFFFE]/50 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-[#0A1A2F]">
            {viewMode === 'scatter' ? t('procBenchTitle') : t('procBenchTitleMedian')}
          </h3>
          {/* [2026-10-13] Say the basis out loud: the figure was whole-contract cost per student
              while this heading and the tooltip both said "per year". It is now annualised by
              initial_term.
              The hidden-deal count that sat here was removed on Tiago's instruction — deals with
              no numeric term are still EXCLUDED (they cannot go on a per-year axis without
              inventing the term), the chart just no longer says how many. The empty state still
              explains it when nothing is plottable. */}
          <p className="text-[11px] text-gray-500 mt-0.5">
            {t('procBenchAnnualised')}{viewMode !== 'scatter' ? ` ${t('procTrendMedianNote')} ${t('procTrendShortTermNote')}` : ''}
          </p>
          {/* [2026-10-13] The "Each point is a contract. Hover for details. Coloured by award
              year. Drag to pan, scroll to zoom." line was removed on Tiago's instruction. The
              vendor-comparison line is kept: it names what the chart is showing when a vendor is
              selected, which the heading alone does not say. */}
          {selectedVendor ? (
            <p className="text-xs text-gray-600 mt-0.5">{t('procVendorVsCompetitors', { vendor: selectedVendor })}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs">
            <button type="button" onClick={() => setViewMode('scatter')} className={"px-2 py-1 rounded " + (viewMode === 'scatter' ? 'bg-[#00C2C2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>{t('procScatter')}</button>
            <button type="button" onClick={() => setViewMode('trend')} className={"px-2 py-1 rounded " + (viewMode === 'trend' ? 'bg-[#00C2C2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>{t('procTrendByCategory')}</button>
            <button type="button" onClick={() => setViewMode('trend_vendor')} className={"px-2 py-1 rounded " + (viewMode === 'trend_vendor' ? 'bg-[#00C2C2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>{t('procTrendByVendor')}</button>
          </div>
        </div>
      </div>
      <div className="p-4 relative">
        {viewMode === 'trend' && ruleOptions.length > 1 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-medium text-gray-700">{t('procTrendSuppliers')}:</span>
            <select
              value={supplierRule.mode}
              onChange={(e) => saveRule({ ...supplierRule, mode: e.target.value })}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
              aria-label={t('procTrendSuppliers')}
            >
              <option value="all">{t('procTrendRuleAll')}</option>
              <option value="exclude">{t('procTrendRuleExclude')}</option>
              <option value="only">{t('procTrendRuleOnly')}</option>
            </select>
            {supplierRule.mode !== 'all' && (
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="flex max-w-[320px] items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-gray-700 hover:bg-gray-50">
                    <span className="truncate">
                      {supplierRule.keys.length === 0
                        ? t('procTrendRuleSearch')
                        : supplierRule.keys.length <= 2
                          ? supplierRule.keys.map((k) => familyMeta.get(k)?.label || k).join(', ')
                          : t(supplierRule.mode === 'exclude' ? 'procTrendRuleSummaryExclude' : 'procTrendRuleSummaryOnly', { n: supplierRule.keys.length })}
                    </span>
                    <ChevronDown className="h-3 w-3 flex-shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-80 p-2">
                  <input
                    type="text"
                    value={ruleSearch}
                    onChange={(e) => setRuleSearch(e.target.value)}
                    placeholder={t('procTrendRuleSearch')}
                    className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-xs"
                  />
                  <div className="max-h-64 overflow-y-auto">
                    {ruleOptions
                      .filter((o) => !ruleSearch || o.label.toLowerCase().includes(ruleSearch.toLowerCase()))
                      .map((o) => {
                        const checked = supplierRule.keys.includes(o.key);
                        return (
                          <label key={o.key} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-gray-50">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => saveRule({
                                ...supplierRule,
                                keys: v ? [...supplierRule.keys, o.key] : supplierRule.keys.filter((k) => k !== o.key),
                              })}
                            />
                            <span className="flex-1 truncate text-gray-800" title={o.label}>{o.label}</span>
                            <span className="tabular-nums text-gray-400">{t('procTrendRuleDeals', { n: o.count.toLocaleString() })}</span>
                          </label>
                        );
                      })}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {(supplierRule.mode !== 'all' || supplierRule.keys.length > 0) && (
              <button type="button" onClick={() => saveRule({ mode: 'all', keys: [] })} className="text-[#007F80] hover:underline">
                {t('procTrendRuleClear')}
              </button>
            )}
            <span className="text-gray-400">{t('procTrendRuleHint')}</span>
          </div>
        )}
        <div
          ref={chartContainerRef}
          className="h-[420px] w-full relative select-none"
          style={{ cursor: viewMode === 'scatter' && isDragging ? 'grabbing' : viewMode === 'scatter' ? 'grab' : 'default' }}
          onMouseDown={handleMouseDown}
          role="presentation"
        >
          {viewMode === 'scatter' && (
            <div className="absolute bottom-2 right-2 z-10 flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleZoomIn}>
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleZoomOut}>
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleReset} title={t('procResetView')}>
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          )}
          {(viewMode === 'trend' || viewMode === 'trend_vendor') ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={
                viewMode === 'trend_vendor'
                  ? trendByVendor.data
                  : trendByCategory.showVendorTrend
                    ? trendByCategory.vendorData
                    : trendByCategory.data
              }
              margin={{ top: 16, right: 24, bottom: 24, left: 24 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="year" stroke="#64748b" style={{ fontSize: 11 }} />
              <YAxis
                domain={[0, yAxisCapTrend]}
                allowDataOverflow
                tickFormatter={(v) => `$${Math.round(Number(v))}`}
                stroke="#64748b"
                style={{ fontSize: 11 }}
              />
              {viewMode === 'trend_vendor' ? (
                <Tooltip content={<VendorTrendTooltip isOwnVendor={isOwnVendor} focused={focusedVendor} />} />
              ) : (
                <Tooltip
                  formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name]}
                  labelFormatter={(l) => t('procYearLabel', { year: l })}
                />
              )}
              {/* Trend by vendor renders its own legend below the chart (clickable, scrollable). */}
              {viewMode !== 'trend_vendor' && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {viewMode === 'trend_vendor'
                ? [...drawnVendors]
                    // The focused line is drawn last so it sits on top of the greyed ones.
                    .sort((a, b) => (a === focusedVendor) - (b === focusedVendor))
                    .map((v) => {
                      const dim = !!focusedVendor && focusedVendor !== v;
                      const on = focusedVendor === v;
                      const own = isOwnVendor(v);
                      return (
                        <Line
                          key={v}
                          type="monotone"
                          dataKey={v}
                          stroke={dim ? '#cbd5e1' : vendorColor(v)}
                          strokeOpacity={dim ? 0.7 : 1}
                          strokeWidth={on ? 4.5 : own ? 4 : dim ? 1.5 : 2}
                          dot={dim ? false : { r: on ? 5 : own ? 4 : 3 }}
                          activeDot={dim ? false : { r: on ? 7 : 5 }}
                          legendType="circle"
                          connectNulls
                          isAnimationActive={false}
                        />
                      );
                    })
                : trendByCategory.showVendorTrend
                  ? [selectedVendor, 'Others'].filter(Boolean).map((key, i) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={i === 0 ? '#00C2C2' : '#94a3b8'}
                        strokeWidth={i === 0 ? 2.5 : 1.5}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    ))
                  : trendByCategory.categories.map((cat, i) => (
                      <Line
                        key={cat}
                        type="monotone"
                        dataKey={cat}
                        stroke={BRAND_COLORS[i % BRAND_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    ))}
            </LineChart>
          </ResponsiveContainer>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 16, right: 24, bottom: 24, left: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                dataKey="enrolment"
                name="Enrolment"
                domain={xDomain}
                allowDataOverflow
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v)))}
                stroke="#64748b"
                style={{ fontSize: 11 }}
              />
              <YAxis
                type="number"
                dataKey="usdPerStudent"
                name="$ per student/year"
                domain={[0, yAxisCapScatter]}
                allowDataOverflow
                tickFormatter={(v) => `$${Math.round(Number(v))}`}
                stroke="#64748b"
                style={{ fontSize: 11 }}
              />
              <ZAxis type="number" dataKey="usdPerStudent" range={[80, 400]} />
              <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3', stroke: '#00C2C2' }} />
              {selectedVendor ? (
                <>
                  <Scatter name="Others" data={scatterData.filter((d) => d.vendor !== selectedVendor)} fill="#94a3b8" stroke="#64748b">
                    {scatterData.filter((d) => d.vendor !== selectedVendor).map((_, i) => (
                      <Cell key={`oth-${i}`} fill="#94a3b8" stroke="#64748b" strokeWidth={0.5} opacity={0.6} />
                    ))}
                  </Scatter>
                  <Scatter name={selectedVendor} data={scatterData.filter((d) => d.vendor === selectedVendor)} fill="#00C2C2" stroke="#0A1A2F">
                    {scatterData.filter((d) => d.vendor === selectedVendor).map((_, i) => (
                      <Cell key={`sel-${i}`} fill="#00C2C2" stroke="#0A1A2F" strokeWidth={1.5} />
                    ))}
                  </Scatter>
                </>
              ) : years.length > 0 ? (
                years.map((year) => (
                  <Scatter
                    key={year}
                    name={year}
                    data={scatterData.filter((d) => d.year === year)}
                    fill={yearColors[year]}
                    stroke={yearColors[year]}
                    onMouseEnter={() => setHoveredYear(year)}
                    onMouseLeave={() => setHoveredYear(null)}
                  >
                    {scatterData
                      .filter((d) => d.year === year)
                      .map((entry, i) => (
                        <Cell
                          key={`${year}-${i}`}
                          fill={yearColors[year]}
                          stroke={hoveredYear === year || !hoveredYear ? '#0A1A2F' : '#d1d5db'}
                          strokeWidth={hoveredYear === year || !hoveredYear ? 1.5 : 0.5}
                          opacity={hoveredYear && hoveredYear !== year ? 0.35 : 1}
                        />
                      ))}
                  </Scatter>
                ))
              ) : (
                <Scatter
                  name="All"
                  data={scatterData}
                  fill="#00C2C2"
                  stroke="#0A1A2F"
                >
                  {scatterData.map((_, i) => (
                    <Cell key={i} fill="#00C2C2" stroke="#0A1A2F" strokeWidth={1} />
                  ))}
                </Scatter>
              )}
            </ScatterChart>
          </ResponsiveContainer>
          )}
        </div>
        {viewMode === 'scatter' && !selectedVendor && (
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {years.map((year) => (
            <div key={year} className="flex items-center gap-1.5 text-xs">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: yearColors[year] ?? '#9ca3af' }}
              />
              <span className="text-gray-700">{year}</span>
            </div>
          ))}
        </div>
        )}
        {viewMode === 'trend_vendor' && trendByVendor.vendors.length > 0 && (
          <div className="mt-2">
            <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>{t('procTrendLegendHint')}</span>
              {focusedVendor && (
                <button type="button" onClick={() => setFocusedVendor(null)} className="text-[#007F80] hover:underline">
                  {t('procTrendLegendClear')}
                </button>
              )}
            </div>
            <div className="flex max-h-28 flex-wrap gap-x-3 gap-y-1 overflow-y-auto rounded border border-gray-100 px-2 py-1.5 text-[11px]">
              {trendByVendor.vendors.map((v) => {
                const drawn = drawnVendors.includes(v);
                const on = focusedVendor === v;
                const dim = !!focusedVendor && !on;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setFocusedVendor((f) => (f === v ? null : v))}
                    className={`flex items-center gap-1 rounded px-1 py-0.5 hover:bg-gray-100 ${on ? 'bg-gray-100 font-semibold text-[#0A1A2F]' : dim ? 'text-gray-400' : 'text-gray-700'}`}
                    title={t('procTrendRuleDeals', { n: (trendByVendor.counts?.[v] ?? 0).toLocaleString() })}
                  >
                    <span
                      className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full border"
                      style={drawn
                        ? { background: dim ? '#cbd5e1' : vendorColor(v), borderColor: dim ? '#cbd5e1' : vendorColor(v) }
                        : { background: 'transparent', borderColor: '#94a3b8' }}
                      aria-hidden="true"
                    />
                    {v}{isOwnVendor(v) ? ` (${t('procYou')})` : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-4 italic">
          {t('procFxFootnote')}
        </p>
      </div>
    </div>
  );
}

export default React.memo(BenchmarkingScatter);
